import React from 'react'

import MemoClose from '../Icons/Close'
import MemoSettingsOutlined from '../Icons/SettingsOutlined'
import MemoWifiOff from '../Icons/WifiOff'
import { IconButton, Menu, TooltipComponent } from '../../../components'
import { useTheme } from '../../../index'
import { useNotificationsHomeContext } from '../../context'
import ConfigPanel from '../ConfigPanel'
import ConfigPopup from '../ConfigPopup'
import NotificationCenterBody from '../NotificationCenterBody'
import { OFFLINE_MESSAGE } from '../../../utils/constants'
import { useBreakpoints } from '../../../hooks/useBreakpoints'
import NotificationCenterFooter from '../NotificationCenterFooter'
import { useNotificationCenterStyles } from '../../../hooks/styles/useNotificationCenterStyles'

const CloseButton = () => {
  const theme = useTheme()
  const {
    data: { errMsg },
    handlers: { handleClosePanel }
  } = useNotificationsHomeContext()

  const { smUp } = useBreakpoints()

  if (!smUp) {
    return (
      <IconButton
        style={{
          color: errMsg
            ? theme.palette.secondary.main
            : theme.palette.text.primary
        }}
        onClick={handleClosePanel}
      >
        <MemoClose fontSize='small' />
      </IconButton>
    )
  }
  return null
}

const ConfigButton = () => {
  const {
    handlers: { handleOpenConfig }
  } = useNotificationsHomeContext()

  return (
    <TooltipComponent title='Notification Preferences'>
      <IconButton onClick={(e) => handleOpenConfig()}>
        <MemoSettingsOutlined fontSize='small' />
      </IconButton>
    </TooltipComponent>
  )
}

const NotificationCenterHeader = () => {
  const {
    data: { errMsg, header, preferenceMode }
  } = useNotificationsHomeContext()

  const {
    NCHeaderContainer,
    NCHeaderTypographyMobile,
    NCHeaderTypography,
    NCHeaderOfflineContainerMobile,
    NCHeaderOfflineContainer
  } = useNotificationCenterStyles()

  const theme = useTheme()
  const { smUp } = useBreakpoints()

  if (header !== undefined || !smUp) {
    return (
      <NCHeaderContainer style={{ flexShrink: 0 }}>
        <NCHeaderTypographyMobile>
          {header === true || header === '' || (!header && !smUp)
            ? 'Notifications'
            : header}
        </NCHeaderTypographyMobile>

        <NCHeaderOfflineContainerMobile>
          {errMsg === 'xhr poll error' ? (
            <TooltipComponent title={OFFLINE_MESSAGE}>
              <MemoWifiOff color='secondary' />
            </TooltipComponent>
          ) : (
            <div />
          )}
          <CloseButton color='secondary' />
        </NCHeaderOfflineContainerMobile>
        {preferenceMode !== 'none' && header && (
          <ConfigButton color='secondary' />
        )}
      </NCHeaderContainer>
    )
  }

  if (errMsg === 'xhr poll error') {
    return (
      <NCHeaderOfflineContainer smUp={smUp} style={{ flexShrink: 0 }}>
        <TooltipComponent title={OFFLINE_MESSAGE}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MemoWifiOff color={theme.palette.text.secondary} />
            <NCHeaderTypography>Offline</NCHeaderTypography>
          </div>
        </TooltipComponent>
        <CloseButton />
      </NCHeaderOfflineContainer>
    )
  } else return null
}

export const NotificationsHomeBody = () => {
  const {
    data: {
      anchorEl,
      showConfig,
      notificationCenterPosition,
      notificationCenterOffset,
      preferenceMode,
      showBranding,
      notificationCenterConfig
    },
    handlers: { handleClosePanel }
  } = useNotificationsHomeContext()

  const { mdUp } = useBreakpoints()

  const { NCMenuContainer, NotificationCenterContainer } =
    useNotificationCenterStyles()

  const getViewportQuadrant = () => {
    if (!anchorEl) {
      return { isRightHalf: false, isBottomHalf: false }
    }
    const rect = anchorEl.getBoundingClientRect()
    return {
      isRightHalf: rect.left + rect.width / 2 > window.innerWidth / 2,
      isBottomHalf: rect.top + rect.height / 2 > window.innerHeight / 2
    }
  }

  const { isRightHalf, isBottomHalf } = getViewportQuadrant()

  const anchorOrigin = mdUp
    ? {
        vertical:
          notificationCenterConfig?.anchorOrigin?.vertical ||
          (isBottomHalf ? 'top' : 'bottom'),
        horizontal:
          notificationCenterConfig?.anchorOrigin?.horizontal ||
          (isRightHalf ? 'right' : 'left')
      }
    : {
        vertical: 'top',
        horizontal: 'left'
      }

  const transformOrigin = mdUp
    ? {
        vertical:
          notificationCenterConfig?.transformOrigin?.vertical ||
          (isBottomHalf ? 'bottom' : 'top'),
        horizontal:
          notificationCenterConfig?.transformOrigin?.horizontal ||
          (isRightHalf ? 'right' : 'left')
      }
    : {
        vertical: 'bottom',
        horizontal: 'right'
      }

  const ConfigComponent =
    preferenceMode === 'embed' ? (
      <ConfigPanel />
    ) : (
      <ConfigPopup preferenceMode={preferenceMode} />
    )

  const handleMenuClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      handleClosePanel()
    }
  }

  return (
    <div>
      <Menu
        className='inapp-container'
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        sx={{
          visibility:
            showConfig && preferenceMode === 'modal' ? 'hidden' : 'visible',
          '& .MuiPaper-root': {
            overflow: 'visible !important',
            backgroundColor: 'transparent',
            boxShadow: 'none'
          },
          '& .MuiList-root': {
            padding: 0,
            backgroundColor: 'transparent'
          },
          borderRadius: '10px',
          boxShadow:
            'rgba(17, 17, 26, 0.05) 0px 4px 16px, rgba(17, 17, 26, 0.05) 0px 8px 32px'
        }}
        onClose={handleMenuClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <NCMenuContainer>
          {!showConfig ? (
            <NotificationCenterContainer
              notificationCenterPosition={notificationCenterPosition}
              notificationCenterOffset={notificationCenterOffset}
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <NotificationCenterHeader />
              <NotificationCenterBody sx={{ flex: 1, overflow: 'auto' }} />
              {showBranding === true ? <NotificationCenterFooter /> : null}
            </NotificationCenterContainer>
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {ConfigComponent}
            </div>
          )}
        </NCMenuContainer>
      </Menu>
    </div>
  )
}

export default NotificationsHomeBody
